import DnaExtractionKitListModal from '@/components/commonComponents/DnaExtractionKitListModal';
import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import {
  useCreateManyExtractionSequences,
  useValidateExtractionSequenceDataFiles,
} from '@/queries';
import { ValidateExtractionSequenceConcentration } from '@/types';
import { downloadSampleFile } from '@/utils';
import { Icon } from '@amili-v3/amili-v3-design';
import { Space, UploadFile, UploadProps } from 'antd';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonConfig } from '../customByConfiguration';
import { DraggerWrapper } from './styles';
import './UploadCsvModal.scss';
import { ScanType } from './utils';

interface IUploadModal {
  isDisabled: boolean;
  extractionBatchId: string;
  isPageLoading?: boolean;
}

const action = ScanType.EXTRACTION_BATCH;

const UploadCsvExtractionSequences = (props: IUploadModal) => {
  const { isDisabled, extractionBatchId, isPageLoading } = props;

  const { t } = useTranslation();

  const [
    bypassAllowDnaExtractionKitSerial,
    setBypassAllowDnaExtractionKitSerial,
  ] = useState<string[]>([]);
  const [showModalConfirmDnaExtracted, setShowModalConfirmDnaExtracted] =
    useState(false);
  const [invalidAllowDnaExtractedValue, setInvalidAllowDnaExtractedValue] =
    useState<ValidateExtractionSequenceConcentration[]>([]);

  const [allowDnaExtractedValue, setAllowDnaExtractedValue] = useState<
    ValidateExtractionSequenceConcentration[]
  >([]);

  const actionKey = action.toLowerCase();

  const { canCreateExtractionBatch: canUpload } = useCheckScopeCombinations([
    [{ entity: ENTITY.EXTRACTION, action: UserActions.CREATE }],
  ]);

  const {
    mutate: mutateValidateExtractionSequenceDataFiles,
    isPending: isPendingValidateExtractionSequenceDataFiles,
  } = useValidateExtractionSequenceDataFiles();

  const {
    mutate: mutateCreateManyExtractionSequences,
    isPending: isPendingCreateManyExtractionSequences,
  } = useCreateManyExtractionSequences();

  const handleExtractionSequenceFilesChange = useCallback(
    (files: RcFile[]) => {
      return mutateValidateExtractionSequenceDataFiles(
        {
          files,
          extractionBatchId,
        },
        {
          onSuccess: data => {
            const invalidAllowDnaExtractionSequences = data.filter(
              item => !item.allowDnaExtraction,
            );

            if (invalidAllowDnaExtractionSequences.length) {
              setInvalidAllowDnaExtractedValue(
                invalidAllowDnaExtractionSequences,
              );
              setAllowDnaExtractedValue(
                data.filter(item => item.allowDnaExtraction),
              );
              setShowModalConfirmDnaExtracted(true);
            } else {
              mutateCreateManyExtractionSequences({
                extractionBatchId,
                extractionSequences: data,
              });
            }
          },
        },
      );
    },
    [
      extractionBatchId,
      mutateCreateManyExtractionSequences,
      mutateValidateExtractionSequenceDataFiles,
    ],
  );

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    maxCount: 1,
    accept: 'text/csv',
    beforeUpload: () => false,
    onChange: (info: UploadChangeParam<UploadFile<any>>) => {
      if (!info?.fileList?.length) return;

      const filesFromList = info.fileList.map(
        file => file.originFileObj as RcFile,
      );
      handleExtractionSequenceFilesChange(filesFromList);
    },
  };

  const handleConfirmDnaExtractedModal = () => {
    const kitUserAllowDnaExtracted = invalidAllowDnaExtractedValue.filter(
      item => bypassAllowDnaExtractionKitSerial.includes(item.kitId),
    );

    const validValue = [...kitUserAllowDnaExtracted, ...allowDnaExtractedValue];
    mutateCreateManyExtractionSequences({
      extractionBatchId,
      turnOnAllowDnaExtraction: Boolean(kitUserAllowDnaExtracted.length),
      extractionSequences: validValue,
    });
    setShowModalConfirmDnaExtracted(false);
    setInvalidAllowDnaExtractedValue([]);
    setBypassAllowDnaExtractionKitSerial([]);
  };

  const handleToggleDnaExtractionBypassForKit = (kitId: string) => {
    setBypassAllowDnaExtractionKitSerial(prev =>
      prev.includes(kitId)
        ? prev.filter(serial => serial !== kitId)
        : [...prev, kitId],
    );
  };

  const handleToggleDnaExtractionBypassForAllKits = () => {
    setBypassAllowDnaExtractionKitSerial(prev =>
      prev.length === invalidAllowDnaExtractedValue.length
        ? []
        : invalidAllowDnaExtractedValue.map(item => item.kitId),
    );
  };

  return (
    <>
      <DraggerWrapper
        {...draggerProps}
        style={{ borderColor: 'var(--link)', padding: 3 }}
        disabled={isDisabled}
        data-testid="uploadCsv"
      >
        <Space className="upload-container">
          <ButtonConfig
            id={`download-sample-${actionKey}`}
            className="download-sample-btn"
            prefix={<Icon name="download" />}
            aria-label="download sample file"
            onClick={(e: any) => {
              e.stopPropagation();
              downloadSampleFile(e, action);
            }}
          >
            {t('common.downloadSampleFile')}
          </ButtonConfig>
          {canUpload && (
            <ButtonConfig
              id={`upload-csv-${actionKey}`}
              type="linkage"
              className="open-file"
              prefix={<Icon name="upload" />}
              disabled={isDisabled}
            >
              {t('titles.uploadCsv')}
            </ButtonConfig>
          )}
        </Space>
      </DraggerWrapper>
      <DnaExtractionKitListModal
        open={showModalConfirmDnaExtracted}
        onCancel={() => {
          setShowModalConfirmDnaExtracted(false);
          setBypassAllowDnaExtractionKitSerial([]);
        }}
        title={t('kit.warningAllowDnaExtraction', {
          AMOUNT: invalidAllowDnaExtractedValue.length,
        })}
        content={t('kit.contentWarningAllowDnaExtraction')}
        kits={invalidAllowDnaExtractedValue.map(item => ({
          key: item.kitId,
          serial: item.kitId.toUpperCase(),
        }))}
        selectable
        selectedKeys={bypassAllowDnaExtractionKitSerial}
        onToggleKitDnaExtractionBypass={handleToggleDnaExtractionBypassForKit}
        onToggleAllKitsDnaExtractionBypass={
          handleToggleDnaExtractionBypassForAllKits
        }
        onConfirm={handleConfirmDnaExtractedModal}
        confirmLoading={isPendingCreateManyExtractionSequences}
        confirmDisabled={
          isPendingValidateExtractionSequenceDataFiles || isPageLoading
        }
      />
    </>
  );
};

export default UploadCsvExtractionSequences;
